export function getVideoMetadata(source: File | string): Promise<{
  width: number;
  height: number;
  durationSeconds: number | null;
} | null> {
  return new Promise((resolve) => {
    const file = typeof source === "string" ? null : source;
    if (file && !file.type.startsWith("video")) {
      resolve(null);
      return;
    }

    // Timeout — if metadata doesn't load in 10 seconds, give up
    const timeout = setTimeout(() => {
      cleanup();
      resolve(null);
    }, 10000);

    const url = file ? URL.createObjectURL(file) : (source as string);
    const el = document.createElement("video");
    el.preload = "metadata";

    const cleanup = () => {
      clearTimeout(timeout);
      if (file) URL.revokeObjectURL(url);
      el.removeAttribute("src");
      el.load();
    };

    el.addEventListener("loadedmetadata", () => {
      const width = el.videoWidth;
      const height = el.videoHeight;
      const rawDuration = el.duration;
      console.log(
        "[VideoMetadata] Extracted:",
        width,
        "x",
        height,
        `(${Math.round(rawDuration)}s)`,
        "from",
        file?.name ?? url,
      );
      cleanup();
      if (!width || !height) {
        resolve(null);
        return;
      }
      const durationSeconds =
        Number.isFinite(rawDuration) && rawDuration > 0
          ? Math.round(rawDuration)
          : null;
      resolve({ width, height, durationSeconds });
    });

    el.addEventListener("error", (e) => {
      console.error("[VideoMetadata] Error loading metadata:", e);
      cleanup();
      resolve(null);
    });

    el.src = url;
  });
}

/**
 * Validate that video dimensions match the expected orientation.
 * Returns an error message string if mismatched, or null if valid.
 */
export function validateVideoOrientation(
  dimensions: { width: number; height: number },
  selectedRatio: "LANDSCAPE" | "PORTRAIT",
): string | null {
  const { width, height } = dimensions;
  if (selectedRatio === "PORTRAIT" && width >= height) {
    return "You selected Portrait but this video appears to be landscape. Please upload a portrait (vertical) video or change the orientation setting.";
  }
  if (selectedRatio === "LANDSCAPE" && height > width) {
    return "You selected Landscape but this video appears to be portrait. Please upload a landscape (horizontal) video or change the orientation setting.";
  }
  return null;
}

/**
 * EVO-72: orientation can be changed after the file was checked at upload.
 * Re-probe each variant's file before save/submit; unreadable files pass.
 */
export async function findOrientationMismatch(
  variants: Array<{
    videoFileUrl?: string | null;
    videoRatio?: "LANDSCAPE" | "PORTRAIT" | null;
  }>,
): Promise<{ index: number; error: string } | null> {
  for (let index = 0; index < variants.length; index++) {
    const v = variants[index];
    if (!v.videoFileUrl || !v.videoRatio) continue;
    const meta = await getVideoMetadata(v.videoFileUrl);
    const error = meta && validateVideoOrientation(meta, v.videoRatio);
    if (error) return { index, error };
  }
  return null;
}
